import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
// import { decode, encode } from "@googlemaps/polyline-codec";
// var simplify = require('simplify-geojson');
import { setChartSettings, setDistance } from '../chartSettings/ChartSettingsSlice'
import { updateRouteCacheStatus, removeFromCache, updateAllRoutes } from '../routeSelection/RouteSelectionSlice'
import { db } from '../../db';
import { getDistance } from 'geolib';
import stravaApi from '../../services/stravaApi';
import { parseGPXElevation, convertGPXToChartData, sampleGPXPoints } from '../../utils/gpxParser';

// Helper function to calculate optimal chunk size for a route
function calculateOptimalChunkSize(routeDistance, chartSettings, targetChunks = 5) {
    const distanceInUserUnits = routeDistance * chartSettings.distMultiplier;
    let optimalChunkSize = Math.round(distanceInUserUnits / targetChunks);

    // Ensure minimum chunk size (at least 1 unit)
    if (optimalChunkSize < 1) {
        optimalChunkSize = 1;
    }

    // For very short routes, use the entire route as one chunk
    if (distanceInUserUnits <= targetChunks) {
        optimalChunkSize = Math.round(distanceInUserUnits);
    }

    return optimalChunkSize;
}

// Helper function to ensure optimal chunk size is set
function ensureOptimalChunkSize(routeDistance, chartSettings, dispatch) {
    // Only calculate optimal chunk size if distance is the default (1000), not set, empty string, or 0
    if (!chartSettings.distance || chartSettings.distance === 1000 || chartSettings.distance === '' || chartSettings.distance === 0) {
        const optimalChunkSize = calculateOptimalChunkSize(routeDistance, chartSettings);
        console.log(`Calculating optimal chunk size: route distance ${routeDistance}m, target 5 chunks, optimal size ${optimalChunkSize}${chartSettings.distAbbr}`);
        dispatch(setDistance(optimalChunkSize));
        return optimalChunkSize;
    }
    console.log(`Using existing chunk distance: ${chartSettings.distance}${chartSettings.distAbbr}`);
    return chartSettings.distance;
}

export const cacheRoute = createAsyncThunk(
    'charts/cacheRoute',
    async ({ routeId, source }, { dispatch, getState }) => {
        // Check if we're offline
        const isOnline = navigator.onLine;
        // If offline and no cached route, show error
        if (!isOnline) {
            throw new Error("You are offline and this route is not cached. Please check your internet connection and try again.");
        }

        // Get source from current route if not provided
        const currentRoute = getState().charts.route_json;
        const routeSource = source || currentRoute?.source || 'rwgps';

        // Use route loader if source is known, otherwise fall back to RWGPS
        if (routeSource === 'strava') {
            // For Strava routes, we need to load and cache the GPX data
            const chartSettings = getState().chartSettings.chartSettings;
            const stravaAuth = getState().auth?.connections?.strava;

            if (!stravaAuth || !stravaAuth.access_token) {
                throw new Error('No Strava authentication available to cache this route.');
            }

            if (stravaApi.isTokenExpired(stravaAuth)) {
                throw new Error('Strava token expired. Please reconnect your Strava account.');
            }

            const stravaToken = stravaAuth.access_token;
            const gpxData = await stravaApi.exportRouteAsGPX(stravaToken, routeId);
            const { elevationPoints, pointsOfInterest, routeName } = parseGPXElevation(gpxData);
            const maxPoints = 2000;
            const sampledPoints = sampleGPXPoints(elevationPoints, maxPoints);
            const trackPoints = convertGPXToChartData(sampledPoints, chartSettings);

            // Process waypoints through calculatePoiRouteDistances if there are any
            let chartData = trackPoints;
            if (pointsOfInterest && pointsOfInterest.length > 0) {
                const { track_points_with_poi } = calculatePoiRouteDistances(pointsOfInterest, trackPoints, chartSettings.distMultiplier);
                chartData = track_points_with_poi;
            }

            // Get route name from route list if available, otherwise use GPX metadata, fallback to placeholder
            const allRoutes = getState().routeSelection.allRoutes;
            const routeFromList = allRoutes.find(route => String(route.id) === String(routeId));
            const finalRouteName = routeFromList?.name || routeName || `Strava Route ${routeId}`;

            const routeInfo = {
                id: String(routeId),
                source: 'strava',
                name: finalRouteName,
                distance: elevationPoints[elevationPoints.length - 1]?.distance || 0,
                // include basic metadata for offline list rendering
                elevation_gain: routeFromList?.elevation_gain ?? 0,
                created_at: routeFromList?.created_at ?? new Date().toISOString(),
                isCached: true
            };

            const cachedAt = new Date().toISOString();
            const routeSpecificChartSettings = { ...chartSettings };
            delete routeSpecificChartSettings.showUserLocation;
            delete routeSpecificChartSettings.unitSystem;
            delete routeSpecificChartSettings.elevUnit;
            delete routeSpecificChartSettings.elevAbbr;
            delete routeSpecificChartSettings.distUnit;
            delete routeSpecificChartSettings.distAbbr;
            delete routeSpecificChartSettings.distMultiplier;
            delete routeSpecificChartSettings.elevMultiplier;

            const cacheKey = getCacheKey(routeId, routeSource);
            await db.routes.put({
                id: cacheKey,
                routeJson: routeInfo,
                cachedAt,
                chartSettings: routeSpecificChartSettings,
                chartData: chartData
            });

            dispatch(updateRouteCacheStatus({
                routeId: String(routeId),
                cachedAt,
            }));

            dispatch(updateCurrentRouteCacheStatus({
                isCached: true,
                cachedAt,
            }));
            return;
        }

        // Handle GPX file routes
        if (routeSource === 'gpx') {
            const chartSettings = getState().chartSettings.chartSettings;
            const currentRoute = getState().charts.route_json;
            const chartData = getState().charts.chartData;

            if (!currentRoute || currentRoute.source !== 'gpx') {
                throw new Error('GPX route data not found. Please upload the GPX file again.');
            }

            if (!chartData || chartData.length === 0) {
                throw new Error('GPX chart data not found. Please upload the GPX file again.');
            }

            const routeInfo = {
                id: String(currentRoute.id),
                source: 'gpx',
                name: currentRoute.name,
                distance: currentRoute.distance,
                elevation_gain: currentRoute.elevation_gain || 0,
                created_at: currentRoute.created_at || new Date().toISOString(),
                isCached: true
            };

            const cachedAt = new Date().toISOString();
            const routeSpecificChartSettings = { ...chartSettings };
            delete routeSpecificChartSettings.showUserLocation;
            delete routeSpecificChartSettings.unitSystem;
            delete routeSpecificChartSettings.elevUnit;
            delete routeSpecificChartSettings.elevAbbr;
            delete routeSpecificChartSettings.distUnit;
            delete routeSpecificChartSettings.distAbbr;
            delete routeSpecificChartSettings.distMultiplier;
            delete routeSpecificChartSettings.elevMultiplier;

            const cacheKey = getCacheKey(currentRoute.id, routeSource);
            await db.routes.put({
                id: cacheKey,
                routeJson: routeInfo,
                cachedAt,
                chartSettings: routeSpecificChartSettings,
                chartData: chartData
            });

            dispatch(updateRouteCacheStatus({
                routeId: String(currentRoute.id),
                cachedAt,
            }));

            dispatch(updateCurrentRouteCacheStatus({
                isCached: true,
                cachedAt,
            }));
            return;
        }

        // Default to RWGPS
        const rwgpsAuth = getState().auth?.connections?.rwgps;
        if (!rwgpsAuth || !rwgpsAuth.access_token) {
            throw new Error('No RWGPS authentication available to cache this route.');
        }

        const token = rwgpsAuth.access_token;
        const response = await fetch(`https://ridewithgps.com/routes/${routeId}.json`, {
            headers: {
                Accept: "application/json",
                "x-rwgps-api-version": 2,
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (response.status === 404) {
            throw new Error("Route not found");
        }
        else if (response.status === 403) {
            throw new Error("API key not valid");
        }

        const route_json = await response.json();
        const cachedAt = new Date().toISOString();
        const chartSettings = getState().chartSettings.chartSettings;

        // get the track_points_with_poi_data and cache it too!!
        const { points_of_interest, track_points } = route_json.route;
        const { track_points_with_poi } = calculatePoiRouteDistances(points_of_interest, track_points, chartSettings.distMultiplier);

        // Create route-specific chart settings by excluding app-wide settings like showUserLocation
        const routeSpecificChartSettings = { ...chartSettings };
        delete routeSpecificChartSettings.showUserLocation;
        delete routeSpecificChartSettings.unitSystem;
        delete routeSpecificChartSettings.elevUnit;
        delete routeSpecificChartSettings.elevAbbr;
        delete routeSpecificChartSettings.distUnit;
        delete routeSpecificChartSettings.distAbbr;
        delete routeSpecificChartSettings.distMultiplier;
        delete routeSpecificChartSettings.elevMultiplier;

        const cacheKey = getCacheKey(routeId, routeSource);
        await db.routes.put({
            id: cacheKey,
            routeJson: { ...route_json.route, isCached: true, source: routeSource },
            cachedAt,
            chartSettings: routeSpecificChartSettings,
            chartData: track_points_with_poi
        });

        // Update the route's cache status in the UI
        dispatch(updateRouteCacheStatus({
            routeId: String(route_json.route.id),
            cachedAt,
        }));

        // Also update the current route's cache status in the charts slice
        dispatch(updateCurrentRouteCacheStatus({
            isCached: true,
            cachedAt,
        }));
    }
)

// Route source loaders - extensible structure for adding new sources
const routeLoaders = {
    strava: async ({ routeId, dispatch, getState }) => {
        const chartSettings = getState().chartSettings.chartSettings;
        const stravaAuth = getState().auth?.connections?.strava;

        if (!stravaAuth) {
            throw new Error('No Strava connection available');
        }

        if (!stravaAuth.access_token) {
            throw new Error('Strava access token not found. Please reconnect your Strava account.');
        }

        // Check if token needs refresh
        if (stravaApi.isTokenExpired(stravaAuth)) {
            throw new Error('Strava token expired. Please reconnect your Strava account.');
        }

        const stravaToken = stravaAuth.access_token;

        // Export route as GPX
        const gpxData = await stravaApi.exportRouteAsGPX(stravaToken, routeId);

        // Parse GPX data to extract elevation points and waypoints
        const { elevationPoints, pointsOfInterest, routeName } = parseGPXElevation(gpxData);

        // Sample points if there are too many (for performance)
        const maxPoints = 2000; // Reasonable limit for chart performance
        const sampledPoints = sampleGPXPoints(elevationPoints, maxPoints);

        // Convert to chart data format
        const trackPoints = convertGPXToChartData(sampledPoints, chartSettings);

        // Calculate optimal chunk size for the route
        const routeDistance = elevationPoints[elevationPoints.length - 1]?.distance || 0;
        const optimalChunkSize = ensureOptimalChunkSize(routeDistance, chartSettings, dispatch);

        // Process waypoints through calculatePoiRouteDistances if there are any
        let trackPointsWithPoi = trackPoints;
        if (pointsOfInterest && pointsOfInterest.length > 0) {
            const { track_points_with_poi } = calculatePoiRouteDistances(pointsOfInterest, trackPoints, chartSettings.distMultiplier);
            trackPointsWithPoi = track_points_with_poi;
        }

        // Create updated chart settings with optimal chunk size
        const updatedChartSettings = { ...chartSettings, distance: optimalChunkSize };

        // Get route name from route list if available, otherwise use GPX metadata, fallback to placeholder
        const allRoutes = getState().routeSelection.allRoutes;
        const routeFromList = allRoutes.find(route => String(route.id) === String(routeId));
        const finalRouteName = routeFromList?.name || routeName || `Strava Route ${routeId}`;

        // Check if route is cached to include cache status
        const cachedRoute = await findCachedRoute(routeId, 'strava');

        // Create route info
        const routeInfo = {
            id: String(routeId),
            source: 'strava',
            name: finalRouteName,
            isCached: !!cachedRoute,
            cachedAt: cachedRoute?.cachedAt || null
        };
        dispatch(setRouteJson(routeInfo));

        // Process the chart data
        dispatch(calcRouteChunks({ trackPointsWithPoi: trackPointsWithPoi, chartSettings: updatedChartSettings }));
    },

    rwgps: async ({ routeId, dispatch, getState }) => {
        const chartSettings = getState().chartSettings.chartSettings;
        const rwgpsAuth = getState().auth?.connections?.rwgps;

        if (!rwgpsAuth || !rwgpsAuth.access_token) {
            throw new Error('No RWGPS authentication available. Please connect your RWGPS account.');
        }

        const token = rwgpsAuth.access_token;
        const response = await fetch(`https://ridewithgps.com/routes/${routeId}.json`, {
            headers: {
                Accept: "application/json",
                "x-rwgps-api-version": 2,
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (response.status === 404) {
            throw new Error("Route not found");
        }
        else if (response.status === 403) {
            throw new Error("API key not valid");
        }

        const route_json = await response.json();

        // Check if route is cached to include cache status
        const cachedRoute = await findCachedRoute(routeId, 'rwgps');

        const routeData = {
            ...route_json.route,
            source: 'rwgps',
            isCached: !!cachedRoute,
            cachedAt: cachedRoute?.cachedAt || null
        };
        dispatch(setRouteJson(routeData));

        // Ensure optimal chunk size is calculated
        const routeDistance = route_json.route.distance;
        const optimalChunkSize = ensureOptimalChunkSize(routeDistance, chartSettings, dispatch);

        const { points_of_interest, track_points } = route_json.route;
        const { track_points_with_poi } = calculatePoiRouteDistances(points_of_interest, track_points, chartSettings.distMultiplier);

        // Create updated chart settings with optimal chunk size
        const updatedChartSettings = { ...chartSettings, distance: optimalChunkSize };
        dispatch(calcRouteChunks({ trackPointsWithPoi: track_points_with_poi, chartSettings: updatedChartSettings }));
    },

    gpx: async ({ routeId, dispatch, getState }) => {
        // For GPX files, first check if route_json is already set and matches
        const currentRoute = getState().charts.route_json;
        if (currentRoute && currentRoute.source === 'gpx' && currentRoute.id === routeId) {
            // Route data already loaded, just ensure chart data is set
            const chartData = getState().charts.chartData;
            if (chartData && chartData.length > 0) {
                return;
            }
        }

        // Try to load from cache (for page refresh scenarios)
        const cachedRoute = await findCachedRoute(routeId, 'gpx');
        if (cachedRoute) {
            const chartSettings = getState().chartSettings.chartSettings;

            dispatch(setRouteJson({
                ...cachedRoute.routeJson,
                isCached: true,
                cachedAt: cachedRoute.cachedAt
            }));

            if (cachedRoute.chartData && cachedRoute.chartData.length > 0) {
                // Merge cached settings with global settings
                const mergedChartSettings = {
                    ...chartSettings,
                    ...cachedRoute.chartSettings,
                };

                const routeDistance = cachedRoute.routeJson.distance;
                let finalDistance = mergedChartSettings.distance;

                if (!mergedChartSettings.distance || mergedChartSettings.distance === 1000 || mergedChartSettings.distance === '' || mergedChartSettings.distance === 0) {
                    const optimalChunkSize = calculateOptimalChunkSize(routeDistance, mergedChartSettings);
                    finalDistance = optimalChunkSize;
                }

                const newChartSettings = {
                    ...mergedChartSettings,
                    distance: finalDistance
                };

                dispatch(setChartSettings(newChartSettings));
                dispatch(calcRouteChunks({ trackPointsWithPoi: cachedRoute.chartData, chartSettings: newChartSettings }));
            }
            return;
        }

        // If we get here, the GPX data wasn't loaded and not in cache
        throw new Error('GPX route data not found. Please upload the GPX file again.');
    }
};

// Helper to generate cache key from source and routeId
function getCacheKey(routeId, source) {
    // For backward compatibility, if source is not provided, use just routeId
    // Otherwise use source:routeId format
    return source ? `${source}:${routeId}` : String(routeId);
}

// Helper to find cached route by source and routeId
async function findCachedRoute(routeId, source) {
    // First try with source if provided
    if (source) {
        const cacheKey = getCacheKey(routeId, source);
        const cachedRoute = await db.routes.get(cacheKey);
        if (cachedRoute) {
            return cachedRoute;
        }
    }

    // Fallback: try without source (backward compatibility)
    const cachedRoute = await db.routes.get(String(routeId));
    if (cachedRoute) {
        return cachedRoute;
    }

    // If source is provided but exact match not found, also check all routes for matching routeId and source
    if (source) {
        const allCachedRoutes = await db.routes.toArray();
        const matchingRoute = allCachedRoutes.find(route =>
            String(route.id) === String(routeId) ||
            (route.routeJson && String(route.routeJson.id) === String(routeId) && route.routeJson.source === source)
        );
        return matchingRoute;
    }

    return null;
}

//from the Cues page, user can change/save the chartSettings but not modify the route cache
export const fetchRoute = createAsyncThunk(
    'charts/fetchRoute',
    async ({ routeId, source }, { dispatch, getState }) => {
        const chartSettings = getState().chartSettings.chartSettings;

        // Check if we're offline
        const isOnline = navigator.onLine;

        // Try to get from IndexedDB first
        const cachedRoute = await findCachedRoute(routeId, source);

        // if the route is cached use the cached route (i.e. loading offline from Cues page)
        if (cachedRoute) {
            dispatch(setRouteJson({
                ...cachedRoute.routeJson,
                isCached: true,
                cachedAt: cachedRoute.cachedAt
            }));
            if (!cachedRoute.chartData || cachedRoute.chartData.length === 0) {
                // console.log('no chartData')
            }
            else {
                // Merge cached settings with global settings first
                const mergedChartSettings = {
                    ...chartSettings, // Start with global settings
                    ...cachedRoute.chartSettings, // Override with route-specific settings
                };

                // Only calculate optimal chunk size if no custom distance is saved
                const routeDistance = cachedRoute.routeJson.distance;
                let finalDistance = mergedChartSettings.distance;

                if (!mergedChartSettings.distance || mergedChartSettings.distance === 1000 || mergedChartSettings.distance === '' || mergedChartSettings.distance === 0) {
                    const optimalChunkSize = calculateOptimalChunkSize(routeDistance, mergedChartSettings);
                    console.log(`Calculating optimal chunk size: route distance ${routeDistance}m, target 5 chunks, optimal size ${optimalChunkSize}${mergedChartSettings.distAbbr}`);
                    finalDistance = optimalChunkSize;
                } else {
                    console.log(`Using saved chunk distance: ${mergedChartSettings.distance}${mergedChartSettings.distAbbr}`);
                }

                const newChartSettings = {
                    ...mergedChartSettings,
                    distance: finalDistance
                };

                dispatch(setChartSettings(newChartSettings))
                dispatch(calcRouteChunks({ trackPointsWithPoi: cachedRoute.chartData, chartSettings: newChartSettings }));
            }
            return;
        }

        // If offline and no cached route, show error
        if (!isOnline) {
            throw new Error("You are offline and this route is not cached. Please check your internet connection and try again.");
        }

        // If source is specified, use the appropriate loader
        if (source && routeLoaders[source]) {
            await routeLoaders[source]({ routeId, dispatch, getState });
            return;
        }

        // If source not specified, try to auto-detect
        // First check if route exists in the route list with a source
        const allRoutes = getState().routeSelection.allRoutes;
        const routeInfo = allRoutes.find(route => String(route.id) === String(routeId));

        if (routeInfo && routeInfo.source && routeLoaders[routeInfo.source]) {
            await routeLoaders[routeInfo.source]({ routeId, dispatch, getState });
            return;
        }

        // Try Strava first if auth is available (since Strava routes are less common)
        const stravaAuth = getState().auth?.connections?.strava;
        if (stravaAuth && stravaAuth.access_token && !stravaApi.isTokenExpired(stravaAuth)) {
            try {
                console.log('Attempting to load from Strava API for route:', routeId);
                await routeLoaders.strava({ routeId, dispatch, getState });
                return;
            } catch (stravaError) {
                console.log('Route not found in Strava, trying RWGPS:', stravaError.message);
                // Continue to RWGPS attempt
            }
        }

        // Fall back to RWGPS (most common case)
        await routeLoaders.rwgps({ routeId, dispatch, getState });
    }
);

export const loadGPXFile = createAsyncThunk(
    'charts/loadGPXFile',
    async ({ gpxData, fileName }, { dispatch, getState }) => {
        const chartSettings = getState().chartSettings.chartSettings;

        // Parse GPX data to extract elevation points and waypoints
        const { elevationPoints, pointsOfInterest, routeName } = parseGPXElevation(gpxData);

        // Sample points if there are too many (for performance)
        const maxPoints = 2000;
        const sampledPoints = sampleGPXPoints(elevationPoints, maxPoints);

        // Convert to chart data format
        const trackPoints = convertGPXToChartData(sampledPoints, chartSettings);

        // Calculate optimal chunk size for the route
        const routeDistance = elevationPoints[elevationPoints.length - 1]?.distance || 0;
        const optimalChunkSize = ensureOptimalChunkSize(routeDistance, chartSettings, dispatch);

        // Process waypoints through calculatePoiRouteDistances if there are any
        let trackPointsWithPoi = trackPoints;
        if (pointsOfInterest && pointsOfInterest.length > 0) {
            const { track_points_with_poi } = calculatePoiRouteDistances(pointsOfInterest, trackPoints, chartSettings.distMultiplier);
            trackPointsWithPoi = track_points_with_poi;
        }

        // Create updated chart settings with optimal chunk size
        const updatedChartSettings = { ...chartSettings, distance: optimalChunkSize };

        // Extract route name from GPX metadata or use filename
        const finalRouteName = routeName || fileName.replace(/\.gpx$/i, '') || 'Uploaded GPX Route';

        // Calculate elevation gain
        let elevationGain = 0;
        for (let i = 1; i < elevationPoints.length; i++) {
            const diff = elevationPoints[i].elevation - elevationPoints[i - 1].elevation;
            if (diff > 0) {
                elevationGain += diff;
            }
        }

        // Generate a unique ID for this GPX upload
        // Use timestamp + sanitized filename to ensure uniqueness
        const timestamp = Date.now();
        const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9]/g, '_').replace(/\.gpx$/i, '').substring(0, 50);
        const uniqueRouteId = `gpx_${timestamp}_${sanitizedFileName}`;

        // Create route info
        const routeInfo = {
            id: uniqueRouteId,
            source: 'gpx',
            name: finalRouteName,
            distance: routeDistance,
            elevation_gain: elevationGain,
            created_at: new Date().toISOString(),
        };
        dispatch(setRouteJson(routeInfo));

        // Process the chart data
        dispatch(calcRouteChunks({ trackPointsWithPoi: trackPointsWithPoi, chartSettings: updatedChartSettings }));

        // Automatically cache the GPX route with updated data
        const cachedAt = new Date().toISOString();
        const routeSpecificChartSettings = { ...updatedChartSettings };
        delete routeSpecificChartSettings.showUserLocation;
        delete routeSpecificChartSettings.unitSystem;
        delete routeSpecificChartSettings.elevUnit;
        delete routeSpecificChartSettings.elevAbbr;
        delete routeSpecificChartSettings.distUnit;
        delete routeSpecificChartSettings.distAbbr;
        delete routeSpecificChartSettings.distMultiplier;
        delete routeSpecificChartSettings.elevMultiplier;

        const cacheKey = getCacheKey(uniqueRouteId, 'gpx');
        try {
            console.log('Saving GPX route to database with cacheKey:', cacheKey);
            const routeToSave = {
                id: cacheKey,
                routeJson: routeInfo,
                cachedAt,
                chartSettings: routeSpecificChartSettings,
                chartData: trackPointsWithPoi
            };
            console.log('Route data to save:', {
                id: routeToSave.id,
                routeName: routeToSave.routeJson.name,
                hasChartData: !!routeToSave.chartData && routeToSave.chartData.length > 0,
                chartDataLength: routeToSave.chartData?.length || 0
            });

            await db.routes.put(routeToSave);

            // Verify the save worked
            const savedRoute = await db.routes.get(cacheKey);
            if (!savedRoute) {
                console.error('Failed to save GPX route to database - route not found after save');
                throw new Error('Failed to save GPX route to database');
            }
            console.log('GPX route successfully saved to database:', {
                id: savedRoute.id,
                routeName: savedRoute.routeJson?.name,
                hasChartData: !!savedRoute.chartData && savedRoute.chartData.length > 0
            });
        } catch (error) {
            console.error('Error saving GPX route to database:', error);
            throw error;
        }

        // Update the route's cache status
        dispatch(updateCurrentRouteCacheStatus({
            isCached: true,
            cachedAt,
        }));

        // Update the route's cache status in the route list
        dispatch(updateRouteCacheStatus({
            routeId: uniqueRouteId,
            cachedAt,
        }));

        // Add the route to the route list if it's not already there
        const allRoutes = getState().routeSelection.allRoutes;
        const routeExists = allRoutes.some(route => String(route.id) === uniqueRouteId && route.source === 'gpx');
        if (!routeExists) {
            const routeToAdd = {
                ...routeInfo,
                isCached: true,
                cachedAt,
            };
            dispatch(updateAllRoutes([...allRoutes, routeToAdd]));
        }

        return { routeId: uniqueRouteId };
    }
);

export const chartsSlice = createSlice({
    name: 'charts',
    initialState: {
        route_json: {},
        original_chart_data: null, // Store the original data
        chunkedChartData: {},
        chartData: [],
        userLocation: {},
        gpxLoading: false,
        gpxError: null,
    },
    reducers: {
        setRouteJson: (state, action) => {
            state.route_json = {
                ...action.payload,
                id: action.payload?.id ? String(action.payload.id) : action.payload?.id
            };
        },
        updateCurrentRouteCacheStatus: (state, action) => {
            const { isCached, cachedAt } = action.payload;
            if (state.route_json && state.route_json.id) {
                state.route_json.isCached = isCached;
                state.route_json.cachedAt = cachedAt;
            }
        },
        clearChartData: (state, action) => {
            state.chunkedChartData = {}
            state.chartData = []
            state.route_json = {}
            state.userLocation = {}
        },
        calcRouteChunks: (state, action) => {
            const { chartSettings, trackPointsWithPoi } = action.payload;
            if (Object.keys(state.route_json).length > 0) {
                state.chartData = trackPointsWithPoi;
                state.chunkedChartData = chunkCharts(trackPointsWithPoi, chartSettings);
            }
        },
        updateUserLocation: (state, action) => {
            const { chartSettings, newLocation } = action.payload;
            if (state.chartData.length > 0 && state.chunkedChartData.chunks.length > 0) {
                // console.log('updating user location', JSON.parse(JSON.stringify(chartSettings)))
                // if (newLocation.accuracy > 100) {
                //     console.warn('accuracy above 100 meters, not showing user location');
                //     state.userLocation = newLocation;
                //     return;
                // }


                const { closest_dist, closest_index } = findUserLocationOnRoute(newLocation, state.chartData, chartSettings.distMultiplier);
                const chunkedChartData = state.chunkedChartData;


                let userIndex = closest_index;
                let userLocationFound = false;
                let newLastLocation = {}
                // look through the chunks to find the user's location
                // we get an absolute index, but we need to find the chunk index of the user
                chunkedChartData.chunks.forEach((chunk, chunkIndex) => {
                    chunkedChartData.chunks[chunkIndex].userInChunk = false;
                    if (userLocationFound) {
                        return;
                    }
                    // is the user in a future chunk? if so, remove this chunk from the userIndex
                    if (userIndex - chunk["d"].length > 0) {
                        userIndex -= chunk["d"].length;
                    } else {
                        // console.log(`the user is in chunkIndex ${chunkIndex} at local index ${userIndex} which is distance ${chunk["d"][userIndex]}`)
                        //add the current location
                        chunkedChartData.chunks[chunkIndex]["n"][userIndex] = "Your Location";
                        //add a POI too?? maybe someday
                        chunkedChartData.chunks[chunkIndex].userInChunk = true;

                        //cleanup the last location
                        if (state.userLocation?.lastLocation) {
                            const lastLocation = state.userLocation.lastLocation
                            if (lastLocation.userIndex !== userIndex) {
                                // console.log('cleaning up last location')
                                chunkedChartData.chunks[lastLocation.chunkIndex]["n"][lastLocation.userIndex] = null;
                            }
                        }
                        newLastLocation = { chunkIndex, userIndex };//store this so we can clean up the last location of the user
                        state.chunkedChartData = chunkedChartData
                        if (state.userLocation?.accuracy > 200) {
                            console.warn(`location accuracy is ${state.userLocation.accuracy}`)
                        } else {
                            // console.log(JSON.stringify(state.userLocation))
                        }
                        userLocationFound = true;
                    }
                })

                state.userLocation = { ...newLocation, lastLocation: newLastLocation, distanceFromRoute: closest_dist.toFixed(2) * 1.0 };
            }
            // else {
            //     console.log('state.chartData.length', state.chartData?.length)
            //     console.log('state.chunkedChartData.chunks.length', state.chunkedChartData?.chunks?.length)
            // }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(removeFromCache.fulfilled, (state, action) => {
                const { routeId } = action.payload;
                // If the current route is being removed from cache
                if (state.route_json && String(state.route_json.id) === String(routeId)) {
                    // For GPX routes, clear the route completely since they can't be re-cached
                    if (state.route_json.source === 'gpx') {
                        state.route_json = {};
                        state.chartData = [];
                        state.chunkedChartData = {};
                    } else {
                        // For other routes, just mark as uncached
                        state.route_json.isCached = false;
                        state.route_json.cachedAt = null;
                    }
                }
            })
            .addCase(loadGPXFile.pending, (state) => {
                state.gpxLoading = true;
                state.gpxError = null;
            })
            .addCase(loadGPXFile.fulfilled, (state) => {
                state.gpxLoading = false;
                state.gpxError = null;
            })
            .addCase(loadGPXFile.rejected, (state, action) => {
                state.gpxLoading = false;
                state.gpxError = action.error?.message || 'Failed to parse GPX file';
            });
    },
})

// Action creators are generated for each case reducer function
export const {
    setRoute,
    setRouteJson,
    updateCurrentRouteCacheStatus,
    poiTextChanged,
    calcRouteChunks,
    updateUserLocation,
    clearChartData,
} = chartsSlice.actions

// Export utility functions for testing
export { chunkCharts };

export default chartsSlice.reducer

function findUserLocationOnRoute(userLocation, chartData, distMultiplier) {

    let closest_dist = 999999999999;
    // let closest_point = null;
    let closest_index = null;

    chartData.forEach((point, i) => {
        const dist = getDistance({ latitude: userLocation.latitude, longitude: userLocation.longitude }, { latitude: point.y, longitude: point.x }) * distMultiplier;

        if (dist < closest_dist) {
            closest_dist = dist;
            // closest_point = point;
            closest_index = i;
        }
    });
    return { closest_index, closest_dist }

}
function calculatePoiRouteDistances(poi_list, track_points, distMultiplier) {
    // performance.mark('starting-calculatePoiRouteDistances')
    // calculate the route distance for each POI
    const poi_with_points = poi_list.map((poi) => {
        // find the closest track point to the POI using euclidean distance from lat/lon
        let closest_dist = 999999999999;
        let closest_point = null;
        track_points.forEach((point) => {
            const dist = getDistance({ latitude: poi.lat, longitude: poi.lng }, { latitude: point.y, longitude: point.x }) * distMultiplier;
            // Math.sqrt(
            //     // This distance formula is WRONG!!
            //     Math.pow(point.y - poi.lat, 2) + Math.pow(point.x - poi.lng, 2)
            // );
            if (dist < closest_dist) {
                closest_dist = dist;
                closest_point = point;
            }
        });

        // console.log('Processing POI:', poi.n, 'Closest point:', closest_point);
        return {
            ...poi,
            e: closest_point.e,
            closest_dist,
            closest_point,
            matched: false,
        };
    });

    // sort by distance d
    poi_with_points.sort((a, b) => {
        return a.d - b.d;
    });

    // fill in the track points with the POI names
    const track_points_with_poi = track_points.map((point) => {
        // find the poi that matches the track point
        const poi = poi_with_points.find(
            (p) => p.closest_point.x === point.x && p.closest_point.y === point.y && !p.matched
        );
        if (poi) {
            // mark the poi as matched on the first match
            poi.matched = true;
        }
        return {
            ...point,
            n: poi ? poi.n : null,       // name
            t: poi ? poi.t : null,       // type
            description: poi ? poi.d : null,  // description
            distanceFromRoute: poi ? poi.closest_dist : null     // distance from route
        };
    });

    // performance.mark("ending-calculatePoiRouteDistances")
    // const poiMeasure = performance.measure("calculatePoiRouteDistances", 'starting-calculatePoiRouteDistances', "ending-calculatePoiRouteDistances")
    // console.log(poiMeasure.duration);
    return { poi_with_points, track_points_with_poi };
}

function chunkCharts(points, chartSettings) {
    // Parse chunk distances - support both single value and comma-separated list
    let chunkDistances = [];
    let isUniformChunking = false;

    if (typeof chartSettings.distance === 'string' && chartSettings.distance.includes(',')) {
        // Handle variable chunk lengths
        chunkDistances = chartSettings.distance.split(',').map(s => s.trim()).filter(s => s !== '').map(s => parseFloat(s)).filter(n => !isNaN(n));
        isUniformChunking = false;
    } else {
        // Handle uniform chunk length (existing behavior)
        const uniformDistance = parseFloat(chartSettings.distance) || 0;
        if (uniformDistance > 0) {
            chunkDistances = [uniformDistance];
            isUniformChunking = true;
        }
    }

    if (chunkDistances.length === 0) {
        return { chunks: [], totalDistance: 0 };
    }

    // chunk up the data into specified chunk distances
    let chunks = [];
    let currentChunkIndex = 0;
    let currentChunkStartDistance = 0;
    let currentChunk = { d: [], e: [], n: [], min: 9999999, poi: [] };

    points.forEach((point, index) => {
        const d = point.d * chartSettings.distMultiplier;
        const e = point.e * chartSettings.elevMultiplier;

        // Check if we need to start a new chunk
        let shouldStartNewChunk = false;

        if (isUniformChunking) {
            // For uniform chunking, continue creating chunks until end of route
            shouldStartNewChunk = d >= currentChunkStartDistance + chunkDistances[0];
        } else {
            // For variable chunking, create chunks based on specified distances
            if (currentChunkIndex < chunkDistances.length) {
                // Check if current chunk has reached its specified distance
                const currentChunkDistance = d - currentChunkStartDistance;
                shouldStartNewChunk = currentChunkDistance >= chunkDistances[currentChunkIndex];

                // Don't start a new chunk if we're at the last point
                if (shouldStartNewChunk && index === points.length - 1) {
                    shouldStartNewChunk = false;
                }
            }
        }

        if (shouldStartNewChunk) {
            // Finish current chunk
            if (currentChunk.d.length > 0) {
                chunks.push(currentChunk);
            }

            // Start new chunk
            if (isUniformChunking) {
                // For uniform chunking, always use the same distance
                currentChunkStartDistance = d;
            } else {
                // For variable chunking, move to next specified distance
                currentChunkIndex++;
                currentChunkStartDistance = d;
            }
            currentChunk = { d: [], e: [], n: [], min: 99999999, poi: [] };
        }

        // Add point to current chunk
        currentChunk.d.push(d);
        currentChunk.e.push(e);
        currentChunk.min = Math.min(currentChunk.min, e);
        currentChunk.n.push(point.n || null);

        if (point.n) {
            currentChunk.poi.push({
                n: point.n,               // name
                d: d,              // distance along route
                offRouteDistance: point.distanceFromRoute,
                t: point.t,               // type
                description: point.description  // description from RWGPS
            });
        }
    });

    // Add the last chunk if it has data
    if (currentChunk.d.length > 0) {
        chunks.push(currentChunk);
    }

    // Calculate elevation gain and loss for each chunk
    chunks.forEach((chunk, chunkIndex) => {
        let elevationGain = 0;
        let elevationLoss = 0;

        // Calculate elevation changes using point sampling to reduce GPS noise
        if (chunk.e.length > 1) {
            // Sample every Nth point to reduce GPS noise and get closer to RWGPS results
            // This is much simpler and more effective than complex noise filtering
            const sampleInterval = 7; // Sample every 10th point
            let cumulativeGain = 0;
            let cumulativeLoss = 0;

            // Process points with sampling interval
            for (let i = sampleInterval; i < chunk.e.length; i += sampleInterval) {
                const currentElevation = chunk.e[i];
                const previousElevation = chunk.e[i - sampleInterval];
                const elevationChange = currentElevation - previousElevation;

                if (elevationChange > 0) {
                    cumulativeGain += elevationChange;
                } else if (elevationChange < 0) {
                    cumulativeLoss += Math.abs(elevationChange);
                }
            }

            // Handle the last point if it's not at a sample interval
            const lastIndex = chunk.e.length - 1;
            if (lastIndex % sampleInterval !== 0) {
                const lastElevation = chunk.e[lastIndex];
                const previousSampledIndex = Math.floor(lastIndex / sampleInterval) * sampleInterval;
                if (previousSampledIndex > 0) {
                    const previousSampledElevation = chunk.e[previousSampledIndex];
                    const finalChange = lastElevation - previousSampledElevation;

                    if (finalChange > 0) {
                        cumulativeGain += finalChange;
                    } else if (finalChange < 0) {
                        cumulativeLoss += Math.abs(finalChange);
                    }
                }
            }

            elevationGain = cumulativeGain;
            elevationLoss = cumulativeLoss;

            // Debug logging (remove in production)
            // if (chunkIndex === 0) {
            // console.log(`Chunk ${chunkIndex + 1} - Sampled gain: ${elevationGain.toFixed(1)}, Sampled loss: ${elevationLoss.toFixed(1)}`);
            // console.log(`Sample interval: ${sampleInterval}, Total points: ${chunk.e.length}, Sampled points: ${Math.ceil(chunk.e.length / sampleInterval)}`);

            // Show sample of the sampling process
            // console.log('Sample of elevation changes (every 10th point):');
            // for (let i = sampleInterval; i < Math.min(sampleInterval * 5, chunk.e.length); i += sampleInterval) {
            //     const change = chunk.e[i] - chunk.e[i - sampleInterval];
            //     console.log(`  Point ${i - sampleInterval} to ${i}: ${change.toFixed(1)} (${change > 0 ? 'gain' : 'loss'})`);
            // }
            // }
        }

        // Add elevation data to chunk
        chunks[chunkIndex].elevationGain = Math.round(elevationGain);
        chunks[chunkIndex].elevationLoss = Math.round(elevationLoss);
        chunks[chunkIndex].max = chunk.e.length > 0 ? Math.max(...chunk.e) : 0;
    });

    // console.log('First chunk POIs:', chunks[0].poi);
    return { chunks: chunks };
}



